import React, { memo, ReactNode, useRef } from 'react';

import { Option } from '@plasma/ui.input.select'
import Modal from '@plasma/ui.layout.modal/dist/modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import Popconfirm from '@plasma/ui.feedback.pop-confirm';
import EditableTable from '../../components/edit-table';
import LoadingComponentScalable from '../../components/Loding-component/LoadingComponentMini';
import TopBarThemed from '../../components/top-bar-themed';
import { planColumns, demandColumns, productDistributionColumns } from '../../types/planned-demand-types';
import usePlannedDemandPageHook from './planned-demand-page.hook';
import * as styled from './planned-demand-page.styles';
import { NormalPlasmaButton, NormalPlasmaSelect } from './planned-demand-page.styles';
import themes from './themes';
import Button from '@plasma/ui.general.button/dist/button';
export interface PlannedDemandPageProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = PlannedDemandPageProps & ThemeOverride;


const EditTableDataTable = (currentDataForTable: any[], dataForColumn: any[]) => {
  if (currentDataForTable && currentDataForTable.length > 0 && currentDataForTable[0] !== undefined) {
    return (
      <EditableTable
        tableData={currentDataForTable || []}
        tableColumns={dataForColumn || []}
        Table_Header=''
        hiddenColumns={["currentWarehouseQuantity"]}
        handleSave={() => { }}
        showActions_ext={false}
        allowSelection={false}
        selectInputReturnType={'value'}
        saveReturnType={'row'}
        onlyAddableFields={['displayseq', 'areacode', 'zonecode', 'equipmentcode', 'consumabledescription', 'consumablecode', 'maxCycles']}
        clearTheSelection
        showSerialNumbers
        ButtonsVisible={{ Edit: false, MoveUp: false, MoveDown: false, InsertUp: false, InsertDown: false, Delete: false }}
        showOnlyEditIcon={false}
      />
    )
  }
  return <></>
}

const PlannedDemandPageTables = ({ planData, productWiseDistributionData, planDemand, isTableLoading }: { planData: any, productWiseDistributionData: any, planDemand: any, isTableLoading: boolean }) => {
  const headersStyles = { marginBottom: '10px', display: 'flex', marginTop: '20px', marginLeft: "20px", alignItems: 'center', color: '#334155', fontSize: '20px', fontWeight: 600 }
  const highlightStyle = {
    color: '#0284c7', 
    backgroundColor: '#e0f2fe', 
    padding: '2px 8px', 
    borderRadius: '6px', 
    fontWeight: 'bold', 
    margin: '0 6px',
    fontSize: '18px'
  };

  const totalProducts = planDemand?.reduce((accumulator: number, currentObject: any) => {
    return accumulator + currentObject.quantityRequired;
  }, 0);

  return (
    <div>
      <h2 style={headersStyles}>
        Production Plan - <span style={highlightStyle}>{planData?.planName || 'N/A'}</span> (<span style={highlightStyle}>{planData?.plannedTonnage || 0}</span> Tons)
      </h2>
      {isTableLoading ? <LoadingComponentScalable borderWidth={10} containerSize={100} initialSeconds={2}/> : EditTableDataTable([planData], planColumns())}
      
      <h2 style={headersStyles}>
        Productwise Distribution - <span style={highlightStyle}>{productWiseDistributionData?.length || 0}</span> Profiles
      </h2>
      {isTableLoading ? <LoadingComponentScalable borderWidth={10} containerSize={100} initialSeconds={2}/> : EditTableDataTable(productWiseDistributionData, productDistributionColumns())}
      
      <h2 style={headersStyles}>
        Consumable Demand - <span style={highlightStyle}>{planDemand?.length || 0}</span> Consumables Type, Total <span style={highlightStyle}>{totalProducts || 0}</span> Consumable Quantity
      </h2>
      {isTableLoading ? <LoadingComponentScalable borderWidth={10} containerSize={100} initialSeconds={4}/> : EditTableDataTable(planDemand, demandColumns())}
    </div>
  );
};

function PlannedDemandPage(props: Props) {
  const { children, className, style, size, themeMode } = props;
  const { t,
    handleAdjustProdMix,
    selectedPlanDemand,
    selectedPlanData,
    isPdfLoading,
    productWiseDistributionData,
    iFrameSrc,
    iFrameVisible,
    itemsAlreadyInCart,
    setIframeVisible,
    fetchAndSetIframeSource,
    handleAddToCart,
    handleJustGoToCart,
    planSnapshotsData,
    selectedSnapshotId,
    handleSnapshotChange,
    listOfPlans,
    handleDeleteSnapShot,
    isTableLoading,
    userRoles,
    isSuperUser
  } = usePlannedDemandPageHook(props);

  const pdfContentRef = useRef<HTMLDivElement>(null);

  const totalPlannedItems = selectedPlanDemand?.reduce((accumulator: number, currentObject: any) => {
    return accumulator + currentObject.quantityRequired;
  }, 0);

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.PlannedDemandPage className={`planned-demand-page ${className ?? ''}`.trim()} style={style}>
        <TopBarThemed topHeading={t("planned-demand")} />
        <styled.ActionContainer>
          <NormalPlasmaButton
            title={t("adjust-production-plan")}
            onClick={handleAdjustProdMix}
            prefixIcon={{ name: 'inventory' }}
          />

          <NormalPlasmaButton
            title={t("generate-pdf")}
            onClick={fetchAndSetIframeSource}
            prefixIcon={{ name: 'picture_as_pdf' }}
            loading={isPdfLoading}
          />

          <NormalPlasmaButton
            title={`${t("add-all")} (${totalPlannedItems} items)`}
            onClick={() => handleAddToCart()}
            prefixIcon={{ name: 'add' }}
          />

          <div style={{
            fontWeight: 'bold',
            padding: '5px 12px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            color: '#475569',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            height: 'fit-content'
          }}>
            {itemsAlreadyInCart} {t("items-in-cart") || "Items in Cart"}
          </div>

          <NormalPlasmaButton
            title={`${t("View")} Cart`}
            onClick={() => handleJustGoToCart()}
            prefixIcon={{ name: 'shopping_cart' }}
          />

          <NormalPlasmaSelect
            value={selectedSnapshotId}
            onChange={handleSnapshotChange}
            placeholder={t("select-calculation") || "Select Calculation"}
            allowClear
            style={{ minWidth: '15vw', flex: 1 }}
          >
            {listOfPlans.map((option) => (
              <Option key={option.key} value={option.key}>
                {option.value}
              </Option>
            ))}
          </NormalPlasmaSelect>

            {(userRoles?.includes('Ts-admin') || isSuperUser) && <Popconfirm
                title={t("delete-calculation") || "Delete Calculation"}
                onConfirm={() => handleDeleteSnapShot()}
                onCancel={() => {}}
                okText={t("yes")}
                cancelText={t("no")}
                >
              <Button
                title={t("delete-calculation") || "Delete Calculation"}
                type={''}
                prefixIcon={{ name: 'delete' }}
                loading={isPdfLoading} htmlType={'button'}
                disabled={listOfPlans?.filter((option: any) => option.key === selectedSnapshotId)[0]?.value?.includes('Plan:')}
                />
            </Popconfirm>}

        </styled.ActionContainer>

        <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: "center",
              width: '100%',
              minWidth: 0,
              overflow: 'hidden'
            }}
          >
            <div
              key={selectedSnapshotId}
              ref={pdfContentRef}
              style={{
                display: 'block',
                width: '100%',
                minWidth: 0,
                overflow: 'hidden'
              }}
            >
            <PlannedDemandPageTables
              planData={selectedPlanData}
              productWiseDistributionData={productWiseDistributionData}
              planDemand={selectedPlanDemand}
              isTableLoading={isTableLoading}
            />
          </div>
        </div>

        <Modal
          title={t("planned-demand")}
          width={'80%'}
          cancelText={t("cancel")}
          visible={iFrameVisible}
          onCancel={() => setIframeVisible(false)}
          footer={null}
        >
          <iframe
            src={iFrameSrc}
            title="PDF"
            style={{ height: "70vh", width: "100%" }} // Set width to 100%
          />
        </Modal>
      </styled.PlannedDemandPage>
    </ThemeProvider>
  );
}

export default memo(PlannedDemandPage) as unknown as typeof PlannedDemandPage;
